

 export default class DataPoint {

    constructor(point, viewer, style) {
            
        // OpenSeadragon viewer
        this.viewer = viewer;
        // OpenSeadragon Points
        this.point = new OpenSeadragon.Point(point.x, point.y);
        // object containing properties borderWidth and borderColor
        this.style = style;
        this.hidden = false;
            
    }
    
    /**
     * Creates a styleobject for displaying a {@link ImageView.DataPoint}.
     * @param	pointStyle:	The style of the point itself. May be constructed by {@link ImageView.DataPoint.getPointStyle(radius, color)}. If undefined, the point will not be rendered.
     * @param	lineStyleX:	The style of the the line through the point, parallel to the x-axis. May be constructed by {@link ImageView.DataPoint.getLineStyle(width, color)}. If undefined, the line will not be rendered.
     * @param	lineStyleY:	The style of the line through the point, parallel to the y-axis. May be constructed by {@link ImageView.DataPoint.getPLineStyle(width, color)}. If undefined, the line will not be rendered.
     */
    static getStyle(pointStyle, lineStyleX, lineStyleY) {
    	return {
    		point: pointStyle,
    		lineX: lineStyleX,
    		lineY: lineStyleY
    	}
    }
    
    /**
     * Creates a styleobject for displaying the point of a {@link ImageView.DataPoint}.
     * @param	radius	The radius of the point in canvas coordinates
     * @param	color	The color of the point
     */
    static getPointStyle(radius, color) {
    	return {
    		pointRadius: radius,
    		pointColor: color
    	}
    }
    
    /**
     * Creates a styleobject for displaying a line of a {@link ImageView.DataPoint}.
     * @param	width	The width of the line in canvas coordinates
     * @param	color	The color of the line
     */
    static getLineStyle(width, color) {
    	return {
    		lineWidth: width,
    		lineColor: color
    	}
    }
    
    /**
     * The rotation of the undelying OpenSeadragon Viewport
     */
    getRotation() {
        return this.viewer.viewport.getRotation();
    }
    
    /**
     * Set the position of the marker to the given x/y coordinates in OpenSeadragon coordinates.
     * If one of x and y is undefined, this position will not be set
     */
    setPosition (x, y) {
    	if(x !== undefined) {
    		this.point.x = x;
    	}
    	if(y !== undefined) {
    		this.point.y = y;
    	}
    }
    
    /**
     * Return the current position as OpenSeadragon.Point - in OpenSeadragon coordinates.
     * Note that the returned point is a clone of the actual position, so changing it will not affect the DataPoint
     */
    getPosition() {
    	return this.point.clone();
    }
    
    /**
     * Removes the point from the canvas. It will no longer be drawn until {@link this.draw()} is called on it
     */
    remove() {
        if(this.eventHandler) {            
            this.viewer.removeHandler( 'update-viewport', this.eventHandler, this );
            this.viewer.forceRedraw();
        }
    }

    /**
     * Adds the point to the canvas, setting it up to be drawn every time the canvas changes
     */
    draw() {
        if(this.eventHandler) {            
            this.viewer.removeHandler( 'update-viewport', this.eventHandler, this );
        }
        _drawPoint({userData: this});
        this.eventHandler = function(event) {
        	_drawPoint(event)
        }
        this.viewer.addHandler( 'update-viewport', this.eventHandler, this );
    }
    
    /**
     * Checks if the given point is either on the datapoint itself or on one of its lines if they are drawn (i.e. associated with a style)
     * @param	point	The point to check
     * @param 	extra	The maximal distance the point may have from the data to count as a hit (in canvas coordinates)
     * @param	pointInCanvas	if true, the given point is assumed to be in canvas coordinates, otherwise in OpenSeadragon coordinates
     */
    contains(point, extra, pointInCanvas) {
    	
    	var pCanvas = pointInCanvas ? ImageView.CoordinateConversion.convertPointFromImageToCanvas(this.point, this.viewer) : this.point;

        var dx = Math.abs(point.x-pCanvas.x);
        var dy = Math.abs(point.y-pCanvas.y);
        
        if( this.style.point && dx <= this.style.point.pointRadius + extra && dy <= this.style.point.pointRadius + extra ){
        	return true;
        } else if(dx <= extra && this.style.lineY) {
        	return true;
        } else if(dy <= extra && this.style.lineX) {
        	return true;
        } else {
        	return false;
        }
    }
    
    /**
     * Gets the {@link ImageView.DataPoint.HitArea} of the point, either the point itself ({@link ImageView.DataPoint.HitArea.POINT}),
     * the line parallel to the x-axis ({@link ImageView.DataPoint.HitArea.LINE_X}) or the line parallel to the y-axis ({@link ImageView.DataPoint.HitArea.LINE_Y}) 
     * If the point is not on any area of the DataPoint, undefined is returned
     * @param	point	The point to check
     * @param 	extra	The maximal distance the point may have from the area to be considered touching it (in canvas coordinates). The point takes precedence over the lines
     * @param	pointInCanvas	if true, the given point is assumed to be in canvas coordinates, otherwise in OpenSeadragon coordinates
     */
    getHitArea(point, extra, pointInCanvas) {
    	
    	var pCanvas = pointInCanvas ? ImageView.CoordinateConversion.convertPointFromImageToCanvas(this.point, this.viewer) : this.point;
    	    	
    	var dx = Math.abs(point.x-pCanvas.x);
        var dy = Math.abs(point.y-pCanvas.y);
        
        if( this.style.point && dx <= this.style.point.pointRadius + extra && dy <= this.style.point.pointRadius + extra ){
        	return imageView.DataPoint.HitAreas.POINT;
        } else if(this.style.lineY && dx <= extra) {
        	return imageView.DataPoint.HitAreas.LINE_Y;
        } else if(this.style.lineX && dy <= extra) {
        	return imageView.DataPoint.HitAreas.LINE_X;
        } else {
        	return undefined;
        }
    }
    
    /**
     * temporarily hide the point, i.e. exlude it from being drawn
     */
    hide( immediate) {
        this.hidden = true;
        if(immediate) {
            this.viewer.forceRedraw();
        }
    }
    
    /**
     * show a formerly hidden point
     */
    show(immediate) {
        this.hidden = false;
        if(immediate) {
            this.viewer.forceRedraw();
        }
    }
    
    /**
     * reads a string in the form "x,y" and interprets it as the coordinates of the point (x,y).
     * Returns an object containing the points as properties x and y; 
     */
    convertStringToPoint(string) {
        var parts = string.split(",");
        if(parts && parts.length == 2) {       
        	return {
        		x : parseFloat(parts[0]),
        		y : parseFloat(parts[1])
        	}
        } else {
            throw "Cannot convert string '" + string + "' to Point";
        }
    }
    
    /**
     * Takes an an object containing the numeric properties x and y and returns it as String in the form "x,y"
     */
    convertPointsToString(point, decimalPlaces) {
        if(!decimalPlaces) {
            decimalPlaces = 0;
        }
        return point.x.toFixed(decimalPlaces) + "," +  point.y.toFixed(decimalPlaces);
    }

    static HitAreas = {
        POINT : "point",
        LINE_Y : "lineY",
        LINE_X : 'lineX',
        isCorner: function( area ) {
            return area === this.POINT;
        },
        isEdge: function( area ) {
            return area === this.LINE_Y || area === this.LINE_X;
        },
        getCursor: function( area, style) {
            if(area === this.POINT && style.point) {
                    return "move";
            } else if(area === this.LINE_X && style.lineX) {
                return "ns-resize";
            } else if(area === this.LINE_Y && style.lineY) {
                    return "ew-resize";
            } else {
                return undefined;
            }
        }
    }
}
    
function _drawPoint(event) {
    var dp = event.userData;
    if(!dp.hidden) {     
        var context = dp.viewer.drawer.context;
        var p = ImageView.CoordinateConversion.convertPointFromImageToCanvas(dp.point, dp.viewer);
        if(dp.style.lineX) {
            //draw line parallel to x
            context.beginPath();
            context.lineWidth = dp.style.lineX.lineWidth;
            context.strokeStyle = dp.style.lineX.lineColor;
            context.moveTo(0, p.y);
            context.lineTo(context.canvas.width, p.y);
            context.stroke();
        }
        if(dp.style.lineY) {
            //draw line parallel to y
            context.beginPath();
            context.lineWidth = dp.style.lineY.lineWidth;
            context.strokeStyle = dp.style.lineY.lineColor;
            context.moveTo(p.x, 0);
            context.lineTo(p.x, context.canvas.height);
            context.stroke();
        }
        if(dp.style.point) {
            //draw point
            context.beginPath();
            context.fillStyle = dp.style.point.pointColor;
            context.lineWidth = 0;
            context.arc(p.x, p.y, dp.style.point.pointRadius, 0, 2*Math.PI);
            context.fill();
        }
    }

}


